import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getEventImage, getPremiumEventImage } from '../utils/eventImages';

const EventCard = ({ event }) => {
  const { 
    id, 
    title, 
    date, 
    city: location, 
    org: organization, 
    venue,
    start,
    end,
    status,
    description,
    image_url,
    event_type,
    event_audience,
    event_category
  } = event;
  
  const [image, setImage] = useState('');
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  
  useEffect(() => {
    // Use database image_url if available, otherwise fall back to generated images
    if (image_url) {
      // Convert HTTP to HTTPS to avoid mixed content issues
      const secureUrl = image_url.replace(/^http:\/\//i, 'https://');
      setImage(secureUrl);
      setIsImageLoading(true);
      setImageError(false);
    } else {
      const premiumImage = getPremiumEventImage(title);
      const finalImage = premiumImage || getEventImage(title);
      setImage(finalImage);
      setIsImageLoading(false);
      setImageError(false);
    }
  }, [title, image_url]);
  
  const handleImageLoad = () => {
    setIsImageLoading(false);
  };
  
  const handleImageError = () => {
    setImageError(true);
    setIsImageLoading(false);
    setImage(getEventImage(title));
  };
  
  // Parse date string as local time to avoid timezone shift
  const parseLocalDate = (dateStr) => {
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date(dateStr);
  };

  // Format date range
  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return 'Date TBD';
    
    const start = parseLocalDate(startDate);
    const end = endDate ? parseLocalDate(endDate) : null;
    
    if (!end || start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    
    if (start.getMonth() === end.getMonth()) {
      return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.getDate()}, ${end.getFullYear()}`;
    }
    
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const formattedDate = formatDateRange(start || date, end);

  const getOrgStyles = (org) => {
    const styles = {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-300',
    };

    if (!org) return styles;

    const orgLower = org.toLowerCase();

    if (orgLower.includes('upci')) {
      return {
        bg: 'bg-purple-100',
        text: 'text-purple-800',
        border: 'border-purple-300',
      };
    }

    if (orgLower.includes('aff')) {
      return {
        bg: 'bg-green-100',
        text: 'text-green-800',
        border: 'border-green-300',
      };
    }

    if (orgLower.includes('aljc')) {
      return {
        bg: 'bg-red-100',
        text: 'text-red-800',
        border: 'border-red-300',
      };
    }

    if (orgLower.includes('aim')) {
      return {
        bg: 'bg-orange-100',
        text: 'text-orange-800',
        border: 'border-orange-300',
      };
    }

    return styles;
  };

  const orgStyles = getOrgStyles(organization);

  // Calculate display status based on dates (client-side)
  const displayStatus = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = parseLocalDate(start || date);
    const endDate = parseLocalDate(end || start || date);
    
    if (status === 'cancelled') return 'cancelled';
    if (endDate < today) return 'past';
    if (startDate <= today && endDate >= today) return 'ongoing';
    return status || 'upcoming';
  }, [start, end, date, status]);

  // Get status badge styles
  // Valid DB statuses: 'draft', 'pending', 'upcoming', 'ongoing', 'past', 'cancelled'
  const getStatusBadge = (displayStatus) => {
    switch (displayStatus) {
      case 'ongoing':
        return { bg: 'bg-blue-500', text: 'text-white', label: 'Happening Now' };
      case 'past':
        return { bg: 'bg-gray-500', text: 'text-white', label: 'Past Event' };
      case 'cancelled':
        return { bg: 'bg-red-500', text: 'text-white', label: 'Cancelled' };
      case 'pending':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' };
      case 'draft':
        return { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' };
      case 'upcoming':
      default:
        return { bg: 'bg-green-100', text: 'text-green-800', label: 'Upcoming' };
    }
  };

  const statusBadge = getStatusBadge(displayStatus);

  // Calculate days to go
  const getDaysToGo = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = parseLocalDate(start || date);
    eventDate.setHours(0, 0, 0, 0);
    const diffTime = eventDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return null;
    if (diffDays === 0) return 'Today!';
    if (diffDays === 1) return '1 day to go';
    return `${diffDays} days to go`;
  };

  const daysToGo = displayStatus === 'upcoming' ? getDaysToGo() : null;

  return (
    <div className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl sm:hover:-translate-y-2 border border-gray-100 dark:border-gray-700 active:scale-[0.98]">
      <Link to={`/events/${id}`} className="block h-full">
        {/* Image */}
        <div className="h-40 sm:h-48 bg-gray-100 overflow-hidden relative">
          {isImageLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="animate-pulse flex space-x-4 w-full h-full">
                <div className="flex-1 space-y-4 py-1">
                  <div className="h-full w-full bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          )}
          <img 
            src={image} 
            alt={title}
            className="w-full h-full object-cover transition-all duration-500 group-hover:scale-105"
            onLoad={handleImageLoad}
            onError={handleImageError}
            loading="lazy"
          />
          {imageError && (
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 flex items-center justify-center">
              <div className="text-center p-4">
                <svg className="w-16 h-16 text-primary/30 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
                <p className="text-xs text-gray-500 mt-1">No image available</p>
              </div>
            </div>
          )}
        </div>
        
        {/* Status Badge */}
        <div className={`absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium ${statusBadge.bg} ${statusBadge.text} ${displayStatus === 'ongoing' ? 'animate-pulse' : ''}`}>
          {statusBadge.label}
        </div>
        
        {/* Organization Badge */}
        {organization && (
          <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-medium ${orgStyles.bg} ${orgStyles.text}`}>
            {organization}
          </div>
        )}
        
        {/* Virtual/Hybrid Badge */}
        {(event_type === 'virtual' || event_type === 'hybrid') && (
          <div className={`absolute top-12 right-3 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${
            event_type === 'virtual' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
          }`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            {event_type === 'virtual' ? 'Virtual' : 'Hybrid'}
          </div>
        )}
        
        {/* Audience Badge */}
        {event_audience && event_audience !== 'general' && (
          <div className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium bg-pink-100 text-pink-800 dark:bg-pink-900/50 dark:text-pink-300">
            {event_audience === 'singles' && '💑 Singles'}
            {event_audience === 'married' && '💍 Married'}
            {event_audience === 'youth' && '🎓 Youth'}
            {event_audience === 'children' && '👶 Children'}
            {event_audience === 'seniors' && '👴 Seniors'}
            {event_audience === 'ministers' && '⛪ Ministers'}
            {event_audience === 'women' && '👩 Women'}
            {event_audience === 'men' && '👨 Men'}
            {event_audience === 'families' && '👨‍👩‍👧‍👦 Families'}
          </div>
        )}
        
        {/* Content */}
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between text-xs mb-2">
            <div className="flex items-center text-gray-500 dark:text-gray-400">
              <svg className="w-4 h-4 mr-1.5 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formattedDate}
            </div>
            {daysToGo && (
              <span className="text-primary font-semibold">{daysToGo}</span>
            )}
          </div>
          
          <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2 line-clamp-2 group-hover:text-primary transition-colors">{title}</h3>
          
          <div className="flex items-center text-gray-600 dark:text-gray-400 text-xs sm:text-sm mb-3">
            {event_type === 'virtual' ? (
              <>
                <svg className="w-4 h-4 mr-1.5 flex-shrink-0 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span className="line-clamp-1">Virtual Event</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className="line-clamp-1">{venue || location || 'Location TBD'}{event.country && event.country !== 'United States' ? `, ${event.country}` : ''}</span>
                {event_type === 'hybrid' && (
                  <span className="ml-1 text-purple-600 font-medium">+ Virtual</span>
                )}
              </>
            )}
          </div>
          
          {description && (
            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 line-clamp-2 mb-3">{description}</p>
          )}
          
          {/* Category Badge */}
          {event_category && event_category !== 'conference' && (
            <div className="mb-3">
              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300 capitalize">
                {event_category}
              </span>
            </div>
          )}
          
          <div className="flex justify-between items-center pt-3 border-t border-gray-100 dark:border-gray-700">
            <span className="inline-flex items-center text-sm font-semibold text-primary group-hover:text-secondary transition-colors">
              View details
              <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
};

export default EventCard;
